import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { supabase } from "../supabaseClient";
import { usePermission } from "../hooks/usePermission";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";
import { startViewAs } from "../utils/viewAs";
import ConfirmModal from "../components/ConfirmModal";
import RolePicker from "../components/RolePicker";
import { apiErrorMessage } from "../utils/timesheets";
import { categoryLabel } from "../utils/permissionLabels";

const inputClass =
  "w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";
const cardClass = "bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-5";

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
const formatDateTime = (value) => (value ? new Date(value).toLocaleString("nl-BE", { dateStyle: "medium", timeStyle: "short" }) : "—");

function initials(profile) {
  const letters = `${(profile.first_name || "")[0] || ""}${(profile.last_name || "")[0] || ""}`;
  return (letters || (profile.email || "?")[0]).toUpperCase();
}

function fullName(profile) {
  return [profile.first_name, profile.last_name].filter(Boolean).join(" ") || profile.email;
}

/**
 * Detailpagina van een gebruiker (/users/:userId) en tegelijk de eigen profielpagina (/profile, `self`).
 * Wijzigingen worden pas bewaard met "Wijzigingen opslaan"; bij verlaten met niet-bewaarde wijzigingen volgt een vraag.
 */
export default function UserDetail({ user, self = false }) {
  const { userId: paramId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const targetId = self ? user?.id : paramId;
  const isOwn = !!user && targetId === user.id;
  const canManage = !self && hasPermission("users.manage");
  const canEditRoles = canManage && hasPermission("roles.view");

  const [profile, setProfile] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [names, setNames] = useState({ first_name: "", last_name: "" });
  const [roleIds, setRoleIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [confirm, setConfirm] = useState(null); // "delete" | "deactivate" | "activate"
  const [password, setPassword] = useState({ current: "", next: "", repeat: "" });
  const [passwordState, setPasswordState] = useState({ busy: false, text: "", type: "" });

  const apply = useCallback((data) => {
    setProfile(data);
    setNames({ first_name: data.first_name || "", last_name: data.last_name || "" });
    setRoleIds(data.roles.map((r) => r.id));
  }, []);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    setLoadError("");
    try {
      const [detail, roles] = await Promise.all([
        apiFetch(`/users/${targetId}`),
        canEditRoles ? apiFetch("/roles") : Promise.resolve([]),
      ]);
      apply(detail);
      setAllRoles(roles);
    } catch (err) {
      setLoadError(apiErrorMessage(err, "Fout bij ophalen gebruiker"));
    } finally {
      setLoading(false);
    }
  }, [targetId, canEditRoles, apply]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ text: "", type: "" }), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const originalRoleIds = useMemo(() => (profile ? profile.roles.map((r) => r.id) : []), [profile]);
  const namesChanged = !!profile && (names.first_name !== (profile.first_name || "") || names.last_name !== (profile.last_name || ""));
  const rolesChanged = canEditRoles && !!profile && !sameSet(roleIds, originalRoleIds);
  const dirty = namesChanged || rolesChanged;
  const { prompt } = useUnsavedChangesGuard(dirty);

  const viewAsUser = async () => {
    try {
      startViewAs(await apiFetch(`/users/${targetId}/view-as`));
      navigate("/");
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Kan niet als deze gebruiker kijken"), type: "error" });
    }
  };

  const permissionGroups = useMemo(() => {
    const groups = {};
    (profile?.permissions || []).forEach((p) => {
      const key = p.category || "overige";
      (groups[key] = groups[key] || []).push(p);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [profile]);

  const discard = () => {
    apply(profile);
    setMessage({ text: "", type: "" });
  };

  const save = async () => {
    if (rolesChanged && roleIds.length === 0) {
      setMessage({ text: "Een gebruiker heeft minstens één rol nodig.", type: "error" });
      return;
    }
    setSaving(true);
    setMessage({ text: "", type: "" });
    try {
      if (namesChanged) {
        const body = JSON.stringify({ first_name: names.first_name, last_name: names.last_name });
        if (canManage) await apiFetch(`/users/${targetId}`, { method: "PATCH", body });
        else await apiFetch("/users/me/profile", { method: "PATCH", body });
      }
      if (rolesChanged) {
        await apiFetch(`/users/${targetId}/roles`, { method: "PUT", body: JSON.stringify({ role_ids: roleIds }) });
      }
      apply(await apiFetch(`/users/${targetId}`));
      setMessage({ text: "Wijzigingen opgeslagen", type: "success" });
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij opslaan"), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (active) => {
    setConfirm(null);
    try {
      await apiFetch(`/users/${targetId}`, { method: "PATCH", body: JSON.stringify({ is_active: active }) });
      apply(await apiFetch(`/users/${targetId}`));
      setMessage({ text: active ? "Gebruiker geactiveerd" : "Gebruiker gedeactiveerd", type: "success" });
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij wijzigen status"), type: "error" });
    }
  };

  const remove = async () => {
    setConfirm(null);
    try {
      await apiFetch(`/users/${targetId}`, { method: "DELETE" });
      navigate("/users");
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij verwijderen"), type: "error" });
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (password.next.length < 6) return setPasswordState({ busy: false, text: "Het nieuwe wachtwoord moet minstens 6 tekens bevatten.", type: "error" });
    if (password.next !== password.repeat) return setPasswordState({ busy: false, text: "De wachtwoorden komen niet overeen.", type: "error" });
    setPasswordState({ busy: true, text: "", type: "" });
    const check = await supabase.auth.signInWithPassword({ email: profile.email, password: password.current });
    if (check.error) return setPasswordState({ busy: false, text: "Het huidige wachtwoord is niet juist.", type: "error" });
    const { error } = await supabase.auth.updateUser({ password: password.next });
    if (error) return setPasswordState({ busy: false, text: error.message || "Wachtwoord wijzigen is mislukt.", type: "error" });
    setPassword({ current: "", next: "", repeat: "" });
    setPasswordState({ busy: false, text: "Wachtwoord gewijzigd.", type: "success" });
  };

  if (loading && !profile) {
    return (
      <div className="p-6" aria-busy="true">
        <div className="h-24 rounded-lg bg-gray-100 animate-pulse mb-5" />
        <div className="h-64 rounded-lg bg-gray-100 animate-pulse" />
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className="p-6">
        <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700">{loadError || "Gebruiker niet gevonden"}</div>
        {!self && <Link to="/users" className="mt-4 inline-block text-sm text-blue-600 hover:underline">← Terug naar gebruikers</Link>}
      </div>
    );
  }

  return (
    <div className="p-6">
      {prompt}
      {confirm === "delete" && (
        <ConfirmModal
          title="Gebruiker verwijderen"
          message={`${fullName(profile)} wordt verwijderd uit het beheerportaal. Het Supabase-account zelf blijft bestaan.`}
          confirmLabel="Verwijderen"
          danger
          onConfirm={remove}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === "deactivate" && (
        <ConfirmModal
          title="Gebruiker deactiveren"
          message={`${fullName(profile)} kan niet meer inloggen tot je de gebruiker opnieuw activeert.`}
          confirmLabel="Deactiveren"
          danger
          onConfirm={() => setActive(false)}
          onCancel={() => setConfirm(null)}
        />
      )}

      {!self && (
        <Link to="/users" className="mb-3 inline-block text-sm text-blue-600 hover:underline">← Gebruikers</Link>
      )}

      {/* Kop */}
      <div className={`${cardClass} flex flex-wrap items-center gap-4`}>
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-600 text-lg font-semibold text-white">
          {initials(profile)}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-bold text-gray-900">{self ? "Mijn profiel" : fullName(profile)}</h1>
          <div className="truncate text-sm text-gray-500">{self ? fullName(profile) + " · " : ""}{profile.email}</div>
        </div>
        {canManage && !isOwn && profile.is_active && (
          <button type="button" onClick={viewAsUser} title="Bekijk de applicatie met de rechten van deze gebruiker, maar met je eigen gegevens"
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            Bekijk als deze gebruiker
          </button>
        )}
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${profile.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>
          {profile.is_active ? "Actief" : "Gedeactiveerd"}
        </span>
      </div>

      {message.text && (
        <div
          role="status"
          className={`mb-4 rounded border px-4 py-3 text-sm ${message.type === "success" ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-700"}`}
        >
          {message.text}
        </div>
      )}

      {/* Gegevens */}
      <section className={cardClass} aria-labelledby="gegevens">
        <h2 id="gegevens" className="mb-4 text-base font-semibold text-gray-800">Gegevens</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="first-name" className={labelClass}>Voornaam</label>
            <input id="first-name" className={inputClass} value={names.first_name} disabled={!(canManage || isOwn)}
              onChange={(e) => setNames({ ...names, first_name: e.target.value })} />
          </div>
          <div>
            <label htmlFor="last-name" className={labelClass}>Naam</label>
            <input id="last-name" className={inputClass} value={names.last_name} disabled={!(canManage || isOwn)}
              onChange={(e) => setNames({ ...names, last_name: e.target.value })} />
          </div>
          <div>
            <label htmlFor="email" className={labelClass}>E-mail</label>
            <input id="email" className={inputClass} value={profile.email || ""} disabled readOnly />
          </div>
          <dl className="grid grid-cols-2 gap-x-4 text-sm">
            <div>
              <dt className={labelClass}>Aangemaakt</dt>
              <dd className="text-gray-800">{formatDateTime(profile.created_at)}</dd>
            </div>
            <div>
              <dt className={labelClass}>Laatste login</dt>
              <dd className="text-gray-800">{formatDateTime(profile.last_login)}</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Rollen */}
      <section className={cardClass} aria-labelledby="rollen">
        <h2 id="rollen" className="mb-1 text-base font-semibold text-gray-800">Rollen</h2>
        <p className="mb-4 text-sm text-gray-500">
          {canEditRoles
            ? "Sleep rollen tussen de lijsten om ze toe te kennen of in te trekken. De rechten van de gebruiker zijn de som van al zijn rollen."
            : "De rechten van een gebruiker zijn de som van al zijn rollen."}
        </p>
        {canEditRoles ? (
          <RolePicker roles={allRoles} value={roleIds} initial={originalRoleIds} onChange={setRoleIds} />
        ) : (
          <div className="flex flex-wrap gap-2">
            {profile.roles.map((r) => (
              <span key={r.id} className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800" title={r.description || ""}>
                {r.name}
              </span>
            ))}
          </div>
        )}
        {canEditRoles && roleIds.length === 0 && (
          <p className="mt-3 text-sm text-red-600">Een gebruiker heeft minstens één rol nodig.</p>
        )}
      </section>

      {/* Effectieve rechten */}
      <section className={cardClass} aria-labelledby="rechten">
        <details>
          <summary id="rechten" className="cursor-pointer text-base font-semibold text-gray-800">
            Effectieve rechten <span className="font-normal text-gray-400">({profile.permissions.length})</span>
          </summary>
          <p className="mt-2 mb-3 text-sm text-gray-500">Wat de gebruiker in totaal mag, met de rol die het recht geeft (zoals opgeslagen).</p>
          <div className="grid gap-4 md:grid-cols-2">
            {permissionGroups.map(([category, items]) => (
              <div key={category}>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {categoryLabel(category)}
                </div>
                <ul className="space-y-1 text-sm">
                  {items.map((p) => (
                    <li key={p.code} className="flex justify-between gap-3">
                      <span className="text-gray-800">{p.name}</span>
                      <span className="shrink-0 text-xs text-gray-400">{p.roles.join(", ")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {permissionGroups.length === 0 && <div className="text-sm text-gray-400">Geen rechten.</div>}
          </div>
        </details>
      </section>

      {/* Koppelingen en historiek */}
      {!self && (
        <div className="grid gap-5 md:grid-cols-2">
          <section className={cardClass} aria-labelledby="koppelingen">
            <h2 id="koppelingen" className="mb-3 text-base font-semibold text-gray-800">Koppelingen</h2>
            <div className="text-sm text-gray-700">
              Personeelslid (prestaties):{" "}
              {profile.linked_member ? (
                <Link to="/timesheets/admin" className="text-blue-600 hover:underline">{profile.linked_member.naam}</Link>
              ) : (
                <span className="text-gray-400">niet gekoppeld</span>
              )}
            </div>
          </section>
          <section className={cardClass} aria-labelledby="historiek">
            <h2 id="historiek" className="mb-3 text-base font-semibold text-gray-800">Recente roltoewijzingen</h2>
            {profile.role_log.length === 0 ? (
              <div className="text-sm text-gray-400">Nog geen wijzigingen.</div>
            ) : (
              <ul className="space-y-1 text-sm">
                {profile.role_log.map((l, i) => (
                  <li key={i} className="flex justify-between gap-3 text-gray-700">
                    <span>
                      <span className={l.action === "added" ? "text-green-700" : "text-red-700"}>{l.action === "added" ? "+" : "−"}</span>{" "}
                      {l.role_name}
                    </span>
                    <span className="shrink-0 text-xs text-gray-400">{formatDateTime(l.created_at)}{l.actor_email ? ` · ${l.actor_email}` : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {/* Beveiliging: enkel op het eigen profiel */}
      {isOwn && (
        <section className={cardClass} aria-labelledby="beveiliging">
          <h2 id="beveiliging" className="mb-4 text-base font-semibold text-gray-800">Wachtwoord wijzigen</h2>
          <form onSubmit={changePassword} className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="pw-current" className={labelClass}>Huidig wachtwoord</label>
              <input id="pw-current" type="password" autoComplete="current-password" className={inputClass} value={password.current}
                onChange={(e) => setPassword({ ...password, current: e.target.value })} required />
            </div>
            <div>
              <label htmlFor="pw-next" className={labelClass}>Nieuw wachtwoord</label>
              <input id="pw-next" type="password" autoComplete="new-password" className={inputClass} value={password.next}
                onChange={(e) => setPassword({ ...password, next: e.target.value })} required minLength={6} />
            </div>
            <div>
              <label htmlFor="pw-repeat" className={labelClass}>Herhaal nieuw wachtwoord</label>
              <input id="pw-repeat" type="password" autoComplete="new-password" className={inputClass} value={password.repeat}
                onChange={(e) => setPassword({ ...password, repeat: e.target.value })} required minLength={6} />
            </div>
            <div className="md:col-span-3 flex items-center gap-3">
              <button type="submit" disabled={passwordState.busy}
                className="rounded bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-50">
                {passwordState.busy ? "Bezig…" : "Wachtwoord wijzigen"}
              </button>
              {passwordState.text && (
                <span role="status" className={`text-sm ${passwordState.type === "success" ? "text-green-700" : "text-red-600"}`}>{passwordState.text}</span>
              )}
            </div>
          </form>
        </section>
      )}

      {/* Gevaarlijke zone */}
      {canManage && !isOwn && (
        <section className="mb-5 rounded-lg border border-red-200 bg-white p-5" aria-labelledby="gevaar">
          <h2 id="gevaar" className="mb-3 text-base font-semibold text-red-700">Gevaarlijke zone</h2>
          <div className="flex flex-wrap gap-3">
            {profile.is_active ? (
              <button onClick={() => setConfirm("deactivate")} className="rounded border border-red-300 px-4 py-2 text-sm text-red-700 hover:bg-red-50">
                Deactiveren
              </button>
            ) : (
              <button onClick={() => setActive(true)} className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Activeren
              </button>
            )}
            <button onClick={() => setConfirm("delete")} className="rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700">
              Verwijderen
            </button>
          </div>
        </section>
      )}

      {/* Opslaan-balk */}
      {dirty && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-white px-5 py-3 shadow-lg" role="region" aria-label="Niet-opgeslagen wijzigingen">
          <span className="text-sm text-gray-700">Je hebt niet-opgeslagen wijzigingen.</span>
          <div className="flex gap-2">
            <button onClick={discard} disabled={saving} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
              Annuleren
            </button>
            <button onClick={save} disabled={saving || (rolesChanged && roleIds.length === 0)}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Bezig…" : "Wijzigingen opslaan"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
